import React from "react";
import { Building2, MapPin } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { useCapability } from "../useCapability";

/**
 * Operational Breadcrumb — Brand · Region · Location.
 *
 * Phase 2 introduces this lightly: contextual operational orientation, not
 * a full hierarchy navigator (Phase 3 will make every segment clickable
 * and add region resolution). For now, segments come from the user's
 * organizational anchor surfaced through the capability map's `scope`.
 *
 * Per design_guidelines.md §2.1 the eyebrow uses Manrope-medium with
 * 0.2em tracking and lowercase; the segments themselves use the body face.
 */
export const Breadcrumb = () => {
    const { user } = useAuth();
    const { map } = useCapability();

    const orgName = user?.organization_name;
    const locationName = user?.location_name;
    const isUnbounded = !!map?.scope?.unbounded;

    // Super-admin: show "Platform" as a context cue rather than a specific org.
    const segments = [];
    if (isUnbounded) {
        segments.push({ icon: Building2, label: "Platform" });
    } else if (orgName) {
        segments.push({ icon: Building2, label: orgName });
    }
    if (locationName) {
        segments.push({ icon: MapPin, label: locationName });
    }

    if (segments.length === 0) return null;

    return (
        <nav
            data-testid="ob-breadcrumb"
            aria-label="Operational scope"
            className="flex items-center gap-2 text-sm text-[--ob-text-secondary]"
            style={{ color: "var(--ob-text-secondary)" }}
        >
            {segments.map((seg, idx) => {
                const Icon = seg.icon;
                return (
                    <React.Fragment key={`${seg.label}-${idx}`}>
                        {idx > 0 && (
                            <span aria-hidden="true" className="opacity-40">
                                /
                            </span>
                        )}
                        <span className="inline-flex items-center gap-1.5">
                            <Icon size={14} strokeWidth={1.5} aria-hidden="true" />
                            <span className="truncate max-w-[200px]">{seg.label}</span>
                        </span>
                    </React.Fragment>
                );
            })}
        </nav>
    );
};
